/**
 * Merchant Instagram feed connection (PE-2178 FE, backend PE-2177).
 *
 * Mirrors watchman's `InstagramConnectionStatusView` / `InstagramPlatformCredentialView`
 * (src/domain/instagram/instagram.service.ts). The access token is encrypted at rest in
 * watchman and never leaves it — nothing here ever carries one.
 */

export const InstagramConnectionStatus = {
  DISCONNECTED: 'DISCONNECTED',
  CONNECTED: 'CONNECTED',
  EXPIRED: 'EXPIRED',
  REVOKED: 'REVOKED',
  ERROR: 'ERROR',
} as const;

export type InstagramConnectionStatus =
  (typeof InstagramConnectionStatus)[keyof typeof InstagramConnectionStatus];

/** `GET me/instagram/status` — watchman synthesises a DISCONNECTED view when no row exists. */
export interface InstagramConnectionStatusView {
  status: InstagramConnectionStatus;
  instagramUsername: string | null;
  feedEnabled: boolean;
  refreshCadenceMinutes: number;
  tokenExpiresAt: string | null;
  connectedAt: string | null;
  lastFetchAt: string | null;
  lastFetchSucceeded: boolean | null;
  lastFetchError: string | null;
}

/**
 * `GET instagram/connections` — the TCS-admin oversight list. A narrower projection than the
 * merchant status view: no `refreshCadenceMinutes`, no `connectedAt`, and the merchant is
 * identified by id only (watchman selects no name), so the page joins against /merchants.
 */
export interface InstagramConnectionSummary {
  merchantId: string;
  instagramUsername: string | null;
  status: InstagramConnectionStatus;
  feedEnabled: boolean;
  tokenExpiresAt: string | null;
  lastFetchAt: string | null;
  lastFetchSucceeded: boolean | null;
  lastFetchError: string | null;
}

/** `PATCH me/instagram/settings`. Cadence is validated 15…10080 minutes server-side. */
export interface UpdateInstagramSettingsPayload {
  feedEnabled?: boolean;
  refreshCadenceMinutes?: number;
}

/** `GET instagram/platform-credential` — null until TCS configures the shared Meta app. */
export interface InstagramPlatformCredential {
  appId: string;
  redirectUri: string;
  /** The secret itself is never returned by any read endpoint. */
  hasSecret: boolean;
}

export interface UpsertInstagramPlatformCredentialPayload {
  appId: string;
  appSecret: string;
  redirectUri: string;
}

export const REFRESH_CADENCE_MIN_MINUTES = 15;
export const REFRESH_CADENCE_MAX_MINUTES = 10080;

export const CONNECTION_STATUS_LABELS: Record<InstagramConnectionStatus, string> = {
  DISCONNECTED: 'Not connected',
  CONNECTED: 'Connected',
  EXPIRED: 'Token expired',
  REVOKED: 'Access revoked',
  ERROR: 'Error',
};

/** Tailwind classes per status, matching the pill convention used across the admin. */
export const CONNECTION_STATUS_CLASSES: Record<InstagramConnectionStatus, string> = {
  DISCONNECTED: 'bg-muted text-muted-foreground',
  CONNECTED: 'bg-green-100 text-green-800',
  EXPIRED: 'bg-amber-100 text-amber-800',
  REVOKED: 'bg-red-100 text-red-800',
  ERROR: 'bg-red-100 text-red-800',
};
